import { AsyncPipe, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { QueryList, ANIMATION_MODULE_TYPE, booleanAttribute, ElementRef, Component, ViewEncapsulation, ChangeDetectionStrategy, Optional, Inject, Input, ContentChildren, NgModule } from '@angular/core';
import { mixinVariant, SbbCommonModule } from '@sbb-esta/angular/core';
import * as i3 from '@sbb-esta/angular/icon';
import { SbbIcon, SbbIconModule } from '@sbb-esta/angular/icon';
import * as i1 from '@angular/cdk/a11y';
import * as i2 from '@angular/cdk/observers';
import { combineLatest } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

/**
 * List of classes to add to SbbButton instances based on host attributes to
 * style as different variants.
 */
const BUTTON_HOST_ATTRIBUTES = [
    'sbb-button',
    'sbb-alt-button',
    'sbb-secondary-button',
    'sbb-ghost-button',
    'sbb-frameless-button',
    'sbb-link',
];
const INDICATOR_ATTRIBUTES = [
    'sbb-button',
    'sbb-secondary-button',
    'sbb-frameless-button',
    'sbb-link',
];
const VALID_ICON_BUTTON_ATTRIBUTES = [
    'sbb-button',
    'sbb-alt-button',
    'sbb-secondary-button',
    'sbb-ghost-button',
];
const DEFAULT_INDICATOR_ICONS = {
    'sbb-button': 'arrow-right-small',
    'sbb-secondary-button': 'arrow-right-small',
    'sbb-frameless-button': 'plus-small',
    'sbb-link': 'arrow-right-small',
};
// Boilerplate for applying mixins to SbbButton.
// tslint:disable-next-line: naming-convention
const _SbbButtonMixinBase = mixinVariant(class {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
    }
});
/**
 * SBB design button.
 */
class SbbButton extends _SbbButtonMixinBase {
    constructor(elementRef, _focusMonitor, _animationMode, _contentObserver) {
        super(elementRef);
        this._focusMonitor = _focusMonitor;
        this._animationMode = _animationMode;
        this._contentObserver = _contentObserver;
        /** Whether this button has an icon indicator. */
        this._hasIconIndicator = this._hasHostAttributes(...INDICATOR_ATTRIBUTES);
        /** Whether the button has the `sbb-icon-button` class */
        this._hasIconButtonClass = false;
        /** Whether the button is disabled. */
        this.disabled = false;
        this._iconRefs = new QueryList();
        // For each of the variant selectors that is present in the button's host
        // attributes, add the correct corresponding class.
        for (const attr of BUTTON_HOST_ATTRIBUTES) {
            if (this._hasHostAttributes(attr)) {
                this._getHostElement().classList.add(attr);
                // Assign the correct default indicator icon.
                if (attr in DEFAULT_INDICATOR_ICONS) {
                    this.svgIcon = DEFAULT_INDICATOR_ICONS[attr];
                }
            }
        }
        // Add a class that applies to all buttons. This makes it easier to target if somebody
        // wants to target all sbb-angular buttons. We do it here rather than `host` to ensure that
        // the class is applied to derived classes.
        elementRef.nativeElement.classList.add('sbb-button-base');
    }
    ngAfterContentInit() {
        this._isIconButton = this._contentObserver.observe(this._elementRef).pipe(startWith([]), map(() => this._hasHostAttributes(...VALID_ICON_BUTTON_ATTRIBUTES) &&
            this._elementRef.nativeElement.textContent.trim() === '' &&
            this._iconRefs.length === 1));
        this._isIconButton.subscribe((isIconButton) => (this._hasIconButtonClass = isIconButton));
        this._leftIconVisible = combineLatest([this.variant, this._isIconButton]).pipe(map(([v, isIconButton]) => !isIconButton && v === 'standard' && this._hasIconIndicator));
        this._rightIconVisible = combineLatest([this.variant, this._isIconButton]).pipe(map(([v, isIconButton]) => !isIconButton &&
            ((v === 'standard' && this._hasIconIndicator) ||
                (v === 'lean' && this._hasHostAttributes('sbb-link')))));
    }
    ngAfterViewInit() {
        this._focusMonitor.monitor(this._elementRef, true);
    }
    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this._elementRef);
    }
    /** Focuses the button. */
    focus(origin, options) {
        if (origin) {
            this._focusMonitor.focusVia(this._getHostElement(), origin, options);
        }
        else {
            this._getHostElement().focus(options);
        }
    }
    _getHostElement() {
        return this._elementRef.nativeElement;
    }
    /** Gets whether the button has one of the given attributes. */
    _hasHostAttributes(...attributes) {
        return attributes.some((attribute) => this._getHostElement().hasAttribute(attribute));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbButton, deps: [{ token: i0.ElementRef }, { token: i1.FocusMonitor }, { token: ANIMATION_MODULE_TYPE, optional: true }, { token: i2.ContentObserver }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbButton, isStandalone: true, selector: "button[sbb-button], button[sbb-alt-button], button[sbb-secondary-button],\n             button[sbb-ghost-button],  button[sbb-frameless-button], button[sbb-link]", inputs: { disabled: ["disabled", "disabled", booleanAttribute], svgIcon: "svgIcon" }, host: { properties: { "attr.disabled": "disabled || null", "class._sbb-animation-noopable": "_animationMode === \"NoopAnimations\"", "class.sbb-disabled": "disabled", "class.sbb-icon-button": "_hasIconButtonClass" } }, queries: [{ propertyName: "_iconRefs", predicate: SbbIcon, read: ElementRef }], exportAs: ["sbbButton"], usesInheritance: true, ngImport: i0, template: "@if ((_leftIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-left-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n<ng-content></ng-content>\n@if ((_rightIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-right-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n", dependencies: [{ kind: "ngmodule", type: SbbIconModule }, { kind: "component", type: i3.SbbIcon, selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbButton, decorators: [{
            type: Component,
            args: [{ selector: `button[sbb-button], button[sbb-alt-button], button[sbb-secondary-button],
             button[sbb-ghost-button],  button[sbb-frameless-button], button[sbb-link]`, exportAs: 'sbbButton', host: {
                        '[attr.disabled]': 'disabled || null',
                        '[class._sbb-animation-noopable]': '_animationMode === "NoopAnimations"',
                        '[class.sbb-disabled]': 'disabled',
                        '[class.sbb-icon-button]': '_hasIconButtonClass',
                    }, inputs: ['disabled'], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [SbbIconModule, AsyncPipe], template: "@if ((_leftIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-left-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n<ng-content></ng-content>\n@if ((_rightIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-right-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.FocusMonitor }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }, { type: i2.ContentObserver }], propDecorators: { svgIcon: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], _iconRefs: [{
                type: ContentChildren,
                args: [SbbIcon, { read: ElementRef }]
            }] } });
/**
 * SBB design anchor button.
 */
class SbbAnchor extends SbbButton {
    constructor(focusMonitor, elementRef, animationMode, _ngZone, contentObserver) {
        super(elementRef, focusMonitor, animationMode, contentObserver);
        this._ngZone = _ngZone;
        this._haltDisabledEvents = (event) => {
            // A disabled button shouldn't apply any actions
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        this._ngZone.runOutsideAngular(() => {
            this._elementRef.nativeElement.addEventListener('click', this._haltDisabledEvents);
        });
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._elementRef.nativeElement.removeEventListener('click', this._haltDisabledEvents);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAnchor, deps: [{ token: i1.FocusMonitor }, { token: i0.ElementRef }, { token: ANIMATION_MODULE_TYPE, optional: true }, { token: i0.NgZone }, { token: i2.ContentObserver }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbAnchor, isStandalone: true, selector: "a[sbb-button], a[sbb-alt-button], a[sbb-secondary-button],\n             a[sbb-ghost-button], a[sbb-frameless-button], a[sbb-link]", inputs: { disabled: "disabled", tabIndex: "tabIndex" }, host: { properties: { "attr.tabindex": "disabled ? -1 : tabIndex", "attr.disabled": "disabled || null", "attr.aria-disabled": "disabled.toString()", "class._sbb-animation-noopable": "_animationMode === \"NoopAnimations\"", "class.sbb-disabled": "disabled" } }, exportAs: ["sbbButton", "sbbAnchor"], usesInheritance: true, ngImport: i0, template: "@if ((_leftIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-left-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n<ng-content></ng-content>\n@if ((_rightIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-right-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n", dependencies: [{ kind: "ngmodule", type: SbbIconModule }, { kind: "component", type: i3.SbbIcon, selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbAnchor, decorators: [{
            type: Component,
            args: [{ selector: `a[sbb-button], a[sbb-alt-button], a[sbb-secondary-button],
             a[sbb-ghost-button], a[sbb-frameless-button], a[sbb-link]`, exportAs: 'sbbButton, sbbAnchor', host: {
                        // Note that we ignore the user-specified tabindex when it's disabled for
                        // consistency with the `sbb-button` applied on native buttons where even
                        // though they have an index, they're not tabbable.
                        '[attr.tabindex]': 'disabled ? -1 : tabIndex',
                        '[attr.disabled]': 'disabled || null',
                        '[attr.aria-disabled]': 'disabled.toString()',
                        '[class._sbb-animation-noopable]': '_animationMode === "NoopAnimations"',
                        '[class.sbb-disabled]': 'disabled',
                    }, inputs: ['disabled'], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [SbbIconModule, AsyncPipe], template: "@if ((_leftIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-left-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n<ng-content></ng-content>\n@if ((_rightIconVisible | async) && svgIcon) {\n  <sbb-icon class=\"sbb-button-indicator-icon sbb-button-right-icon\" [svgIcon]=\"svgIcon\"></sbb-icon>\n}\n" }]
        }], ctorParameters: () => [{ type: i1.FocusMonitor }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }, { type: i0.NgZone }, { type: i2.ContentObserver }], propDecorators: { tabIndex: [{
                type: Input
            }] } });

class SbbButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbButtonModule, imports: [CommonModule, SbbCommonModule, SbbIconModule, SbbButton, SbbAnchor], exports: [SbbButton, SbbAnchor] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbButtonModule, imports: [CommonModule, SbbCommonModule, SbbIconModule, SbbButton, SbbAnchor] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SbbCommonModule, SbbIconModule, SbbButton, SbbAnchor],
                    exports: [SbbButton, SbbAnchor],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SbbAnchor, SbbButton, SbbButtonModule };
